/**
 * AI Insight output may contain ISO dates (`2026-03-04`) or timestamps
 * (`2026-03-04T10:30:00Z`), both as dedicated date fields and inside free text.
 * Users must only ever see British `DD-MM-YYYY` dates, so every insight is
 * normalised once, where it enters the UI, rather than at each render site.
 * Values already in `DD-MM-YYYY` are left untouched.
 */
const ISO_DATE_PATTERN =
  /(?<![\d-])(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?(?![\d-])/g;

const pad = (value: number) => String(value).padStart(2, "0");

function formatMatch(
  match: string,
  year: string,
  month: string,
  day: string,
  hour: string | undefined
): string {
  if (hour === undefined) {
    return `${day}-${month}-${year}`;
  }
  const parsed = new Date(match);
  if (Number.isNaN(parsed.getTime())) {
    return `${day}-${month}-${year}`;
  }
  return `${pad(parsed.getDate())}-${pad(parsed.getMonth() + 1)}-${parsed.getFullYear()} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

/** Rewrites every ISO date or timestamp inside a string to British format. */
export function formatIsoDatesInText(text: string): string {
  return text.replace(ISO_DATE_PATTERN, (match, year, month, day, hour) =>
    formatMatch(match, year, month, day, hour)
  );
}

/** Returns a copy of `value` with every ISO date in any nested string rewritten. */
export function normalizeInsightDates<T>(value: T): T {
  if (typeof value === "string") {
    return formatIsoDatesInText(value) as T;
  }
  if (Array.isArray(value)) {
    return value.map(normalizeInsightDates) as T;
  }
  if (value != null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key, normalizeInsightDates(child)])
    ) as T;
  }
  return value;
}

const BRITISH_DATE_PATTERN = /^(\d{2})-(\d{2})-(\d{4})(?: \d{2}:\d{2})?$/;

/**
 * Parses a normalised `DD-MM-YYYY` (optionally followed by ` HH:mm`) value to a
 * UTC-midnight Date for calendar arithmetic. Returns null for anything else,
 * including impossible dates. Never falls back to `new Date(string)`, which
 * would read `05-04-2026` as 4 May.
 */
export function parseBritishDate(value: string): Date | null {
  const match = BRITISH_DATE_PATTERN.exec(value.trim());
  if (match == null) {
    return null;
  }
  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
    ? parsed
    : null;
}
